import { useFormatter } from '@api/useFormatter'
import type { RouteRow } from '@components/DriverTracking/Routes/api/schema'
import { useRouteAPI } from '@components/DriverTracking/Routes/api/useRouteAPI'
import { LocationSelector } from '@components/DriverTracking/Routes/components/RouteGrid/LocationSelector'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { GraviGrid, Horizontal } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { useQueryClient } from '@tanstack/react-query'
import api from '@utils/api'
import type { GridApi } from 'ag-grid-community'
import { type MutableRefObject, useEffect, useMemo, useRef, useState } from 'react'
import { getColumnDefs } from './columnDefs'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'route-grid'

export function RouteGrid() {
  const gridAPIRef = useRef() as MutableRefObject<GridApi<RouteRow>>
  const queryClient = useQueryClient()
  const { updateSetting, getSetting } = useUserSettings()
  const { formatDistance } = useFormatter()
  const { useRouteOverview, useRouteMetadata, useRouteUpdate } = useRouteAPI()
  const columnDefs = getColumnDefs(formatDistance)
  const { data: metadata } = useRouteMetadata()
  const locationOptions = useMemo(() => metadata?.locations, [metadata])
  const [selectedLocationID, setSelectedLocation] = useState<string | null>(null)

  useEffect(() => {
    const validOptions = locationOptions?.map((option: any) => option.id)
    if (!validOptions) return

    getSetting('route-management-selectedLocationID').then((value) => {
      if (validOptions?.includes(value)) setSelectedLocation(value)
    })
  }, [locationOptions])

  const excelOptions = useMemo(
    () => [
      {
        title: 'Download Route Overrides',
        fileName: 'Route Overrides.xlsx',
        downloadEP: () => api.downloadRoutes(selectedLocationID),
      },
      { title: 'Upload Route Overrides', uploadEP: api.uploadRoutes },
    ],
    [selectedLocationID]
  )

  async function handleUpdate(values: RouteRow | RouteRow[]) {
    if (Array.isArray(values)) return // Routes pages doesn't support bulk updates
    const payload = {
      id: values._id,
      time_override: values.override_travel_time_minutes,
      distance_override: values.override_distance_miles,
      payroll_distance_override: values.override_payroll_miles,
      override_payroll_minutes: values.override_payroll_minutes,
    }
    const result = await useRouteUpdate.mutateAsync(payload)
    gridAPIRef?.current?.applyTransaction({ update: [result] })
  }

  const { data, isFetching: isFetchingOverview } = useRouteOverview(selectedLocationID)

  const agPropOverrides = useMemo(() => {
    return {
      // @ts-ignore. _id exists?
      getRowId: (row) => row.data?._id,
      groupDisplayType: 'multipleColumns',
      suppressAggFuncInHeader: true,
      getContextMenuItems: () => {
        const contextMenuItems = ['copy', 'copyWithHeaders', 'paste', 'separator', 'export']
        return contextMenuItems
      },
    } satisfies GraviGridProps<RouteRow, {}>['agPropOverrides']
  }, [data])

  const controlBarProps = useMemo(() => {
    return {
      title: 'Routes',
      actionButtons: (
        <Horizontal verticalCenter style={{ gap: '1em' }}>
          <UploadDownloadButtons
            refreshCallback={() => {
              gridAPIRef.current?.deselectAll()
              queryClient
                .invalidateQueries({
                  queryKey: ['routeOverview'],
                })
                .then(() => gridAPIRef.current?.getDisplayedRowAtIndex(0)?.setSelected(true))
            }}
            excelOptions={excelOptions}
            fileName='Pay Mileage Bands.xlsx'
          />
          <LocationSelector
            locationOptions={locationOptions}
            selectedLocationID={selectedLocationID}
            setSelectedLocation={setSelectedLocation}
            updateSetting={updateSetting}
            updateSettingKey='route-management-selectedLocationID'
          />
        </Horizontal>
      ),
    }
  }, [locationOptions, selectedLocationID])

  return (
    <GraviGrid
      columnDefs={columnDefs}
      agPropOverrides={agPropOverrides}
      controlBarProps={controlBarProps}
      primaryKey='_id'
      storageKey={STORAGE_KEY}
      onGridConfigChanged={(config: unknown) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      loading={isFetchingOverview}
      rowData={data}
      externalRef={gridAPIRef}
      updateEP={handleUpdate}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}
